import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  untracked,
  ViewChild
} from '@angular/core';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../shared/store/app.reducers';
import {NotificationService} from '../../../../shared/service/notification.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {NotificationSignalStore} from '../../../../shared/store/notification/notification.signal-store';
import {Location, LowerCasePipe} from '@angular/common';
import {IconComponent} from '../../../../shared/component/icon/icon.component';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'ucs-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IconComponent, FormsModule, LowerCasePipe, TranslatePipe]
})
export class NotificationComponent implements OnInit, OnDestroy {
  @ViewChild('notificationModalTemplate', {static: true}) notificationModal: TemplateRef<any>;
  activeModalRef: NgbModalRef;
  allNotifications: NotificationDto[];
  notificationApproved = false;
  currentNotification: NotificationDto;
  currentNotificationIndex = 0;
  appScope: AppScope;
  private readonly notificationSignalStore = inject(NotificationSignalStore);
  private store = inject<Store<fromRoot.AppState>>(Store);
  private notificationService = inject(NotificationService);
  private modalService = inject(NgbModal);
  private location = inject(Location);
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor() {
    effect(() => {
      const userNotifications = this.notificationSignalStore.userNotifications();
      untracked(() => this.showUserNotifications(userNotifications));
    });
  }

  ngOnInit(): void {

    //Init user notifications
    this.store.select(fromRoot.getUserInfo)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(userInfo => {
        this.defineAppScope(userInfo);
        if (this.location.path()?.startsWith('/maintenance')) {
          this.appScope = 'SALES_MAINTENANCE';
        }

        this.notificationSignalStore.fetchUserNotifications(this.appScope);
      });
  }

  toggleNotificationApproved() {
    this.notificationApproved = !this.notificationApproved;
  }

  approveNotification() {
    this.notificationService.approveUserNotification(this.currentNotification.id)
      .subscribe(() => {
        this.activeModalRef.close();
        this.showNextNotification();
      });
    this.notificationApproved = false;
  }

  showNextNotification() {
    this.currentNotificationIndex += 1;
    if (this.allNotifications?.length > 0 && this.currentNotificationIndex < this.allNotifications.length) {
      this.currentNotification = this.allNotifications[this.currentNotificationIndex];
      this.displayCurrentNotification();
    } else {
      this.currentNotificationIndex = 0;
    }
  }

  displayCurrentNotification() {
    this.activeModalRef = this.modalService.open(this.notificationModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  /**
   * Unsubscribe from Observables when component is destroyed to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private showUserNotifications(userNotifications: NotificationDto[] | undefined) {
    this.allNotifications = userNotifications;
    if (!this.allNotifications || this.allNotifications.length === 0) {
      return;
    }
    this.currentNotification = this.allNotifications[this.currentNotificationIndex];
    this.displayCurrentNotification();
  }

  private defineAppScope(userInfo: UserInfoDto) {
    let seller = false;
    let buyer = false;
    for (const element of userInfo.roles) {
      if (element.includes('SELLER')) {
        seller = true;
      }
      if (element.includes('BUYER')) {
        buyer = true;
      }
    }

    if (!buyer && seller) {
      this.appScope = 'SALES_MAINTENANCE';
    } else {
      this.appScope = 'SALES_STORE';
    }
  }
}
